import {RoomState} from '@lib/infrastructure';
import {RoomAvailabilityStatus} from '../../../../../submodules/map/mapiq-map/markers/NodeMarkerData';
import {MeetingRoom, MeetingRoomSuggestion, MeetingRoomSuggestionsStateType} from '../..';

const meetingRoomSuggestionScoreThreshold = 0.3;

export const isAboveRequiredScoreThreshold = (score?: number) => (score ?? -1) >= meetingRoomSuggestionScoreThreshold;

type MarkerState = 'highlighted' | 'dimmed' | undefined;

export const getMarkerState = (meetingRoomSuggestion?: MeetingRoomSuggestion): MarkerState => {
  const isBookedLocally = meetingRoomSuggestion?.markedAsBookedLocally;
  const roomState = isBookedLocally ? RoomState.Booked : meetingRoomSuggestion?.state;

  switch (roomState) {
    case RoomState.NotBookedAndFree:
    case RoomState.Free:
    case RoomState.BookingAboutToEndAndFree:
    case RoomState.NotBooked:
    case RoomState.NotBookedAndUnknownSensor:
    case RoomState.BookingAboutToStartAndFree:
    case RoomState.UnknownSensor:
    case RoomState.BookingAboutToStartAndUnknownSensor:
    case RoomState.Untraceable:
      return 'highlighted';
    case RoomState.NotBookedAndInUse:
    case RoomState.BookingAboutToEnd:
    case RoomState.BookingAboutToEndAndInUse:
    case RoomState.BookingAboutToEndAndUnknownSensor:
    case RoomState.BookedAndFree:
    case RoomState.BookingAboutToStart:
    case RoomState.BookingAboutToStartAndInUse:
      return 'dimmed';
    case RoomState.BookedAndUnknownSensor:
    case RoomState.BookedAndInUse:
    case RoomState.InUse:
    case RoomState.Booked:
    default:
      return;
  }
};

export const groupByRoomStateAvailabilty = (meetingRoomSuggestion?: MeetingRoomSuggestion) => {
  const isBookedLocally = meetingRoomSuggestion?.markedAsBookedLocally;
  const roomState = isBookedLocally ? RoomState.Booked : meetingRoomSuggestion?.state;

  switch (roomState) {
    case RoomState.NotBookedAndFree:
    case RoomState.Free:
    case RoomState.BookingAboutToEndAndFree:
    case RoomState.NotBooked:
    case RoomState.NotBookedAndUnknownSensor:
    case RoomState.BookingAboutToStartAndFree:
    case RoomState.UnknownSensor:
    case RoomState.BookingAboutToStartAndUnknownSensor:
    case RoomState.Untraceable:
      return 'available';
    case RoomState.NotBookedAndInUse:
    case RoomState.BookingAboutToEnd:
    case RoomState.BookingAboutToEndAndInUse:
    case RoomState.BookingAboutToEndAndUnknownSensor:
    case RoomState.BookedAndFree:
    case RoomState.BookingAboutToStart:
    case RoomState.BookingAboutToStartAndInUse:
      return 'occupied';
    case RoomState.BookedAndUnknownSensor:
    case RoomState.BookedAndInUse:
    case RoomState.InUse:
    case RoomState.Booked:
    default:
      return 'unknown';
  }
};

const assertUnreachable = (x: never) => {
  throw new Error(`Unreachable state: ${x}`);
};

export const getAvailabilityStatus = (meetingRoomSuggestion?: MeetingRoomSuggestion): RoomAvailabilityStatus => {
  const isBookedLocally = meetingRoomSuggestion?.markedAsBookedLocally;
  const roomState = isBookedLocally ? RoomState.Booked : meetingRoomSuggestion?.state;

  switch (roomState) {
    // Available
    case RoomState.Free:
    case RoomState.NotBooked:
    case RoomState.NotBookedAndFree:
    //// Likely about to be available
    case RoomState.BookingAboutToStart:
    case RoomState.BookingAboutToStartAndFree:
    case RoomState.NotBookedAndUnknownSensor:
      return 'available';

    // Not suggested
    case RoomState.InUse:
    case RoomState.Booked:
    case RoomState.BookedAndInUse:
    case RoomState.NotBookedAndInUse:
    case RoomState.BookingAboutToStartAndInUse:
    case RoomState.BookingAboutToEndAndInUse:
    case RoomState.BookedAndUnknownSensor:
      return 'occupied';

    // Custom state for no sensor or booking data
    case RoomState.UnknownSensor:
    case RoomState.Untraceable:
    case undefined: // No room suggestion data
      return 'untraceable';

    // Seems available
    case RoomState.BookedAndFree:
    case RoomState.BookingAboutToEndAndFree:
    case RoomState.BookingAboutToEnd:
    //// Other 'unknown' states
    case RoomState.BookingAboutToEndAndUnknownSensor:
    case RoomState.BookingAboutToStartAndUnknownSensor:
    case RoomState.Unknown:
      return 'unknown';

    // Unhandled cases
    default:
      return assertUnreachable(roomState);
  }
};

export const getAvailabilityStatusFromData = (
  entities: MeetingRoomSuggestionsStateType | undefined,
  room: MeetingRoom,
): RoomAvailabilityStatus => {
  const state = entities?.data[room.suggestionData!];
  return getAvailabilityStatus(state);
};
